import { useState, useEffect, useRef, useCallback } from "react";
import { useDispatch } from "react-redux";

import { mainLog, LOG_LEVEL } from "services/logService";
import { logout } from "services/utility/authentication";

import { closeAlert } from "globalState/alertSlice";
import globalState from "globalState/globalState";
import { closeModal } from "globalState/modalSlice";

import {
  MINUTES,
  TIMEOUT_WARNING_TIME_DEFICIT,
  LAST_ACTIVITY_KEY,
  TIMEOUT_STORAGE_UPDATE_THROTTLE,
} from "appConstants";

const events = [
  "mousemove",
  "keydown",
  "wheel",
  "DOMMouseScroll",
  "mouseWheel",
  "mousedown",
  "touchstart",
  "touchmove",
  "MSPointerDown",
  "MSPointerMove",
  "visibilitychange",
];

function debounced(fn, delay) {
  let timerId;
  return function (...args) {
    if (timerId) {
      clearTimeout(timerId);
    }
    timerId = setTimeout(() => {
      fn(...args);
      timerId = null;
    }, delay);
  };
}

export function useInactivityTimeout() {
  const dispatch = useDispatch();
  const [timeoutWarning, setTimeoutWarning] = useState(false);

  const { inactivityTimeoutMinutes } =
    globalState.getState().userCompanySettings.settings;

  const lastLocalActivityRef = useRef(0);
  const lastStorageUpdateRef = useRef(0);
  const debouncedUpdateActivityRef = useRef(null);

  const handleLogout = useCallback(() => {
    dispatch(closeModal());
    dispatch(closeAlert());
    setTimeoutWarning(false);
    logout();
  }, [dispatch]);

  useEffect(() => {
    const intervalRef = { current: null };

    function updateLastActivity() {
      const timestamp = Date.now();
      lastLocalActivityRef.current = timestamp;

      if (
        timestamp - lastStorageUpdateRef.current >=
        TIMEOUT_STORAGE_UPDATE_THROTTLE
      ) {
        localStorage.setItem(LAST_ACTIVITY_KEY, timestamp.toString());
        lastStorageUpdateRef.current = timestamp;
      }
    }

    function checkTimeout() {
      if (
        window.location.pathname.includes("/login") ||
        inactivityTimeoutMinutes <= 0
      ) {
        return;
      }

      const stored = localStorage.getItem(LAST_ACTIVITY_KEY);
      const storageTimestamp = stored ? parseInt(stored, 10) : 0;
      const lastActivityTimestamp = Math.max(
        lastLocalActivityRef.current,
        storageTimestamp
      );
      const timeElapsed = Date.now() - lastActivityTimestamp;

      const totalLogoutTime = inactivityTimeoutMinutes * MINUTES;
      const totalWarnTime = totalLogoutTime - TIMEOUT_WARNING_TIME_DEFICIT;

      if (timeElapsed >= totalLogoutTime) {
        handleLogout();
      } else if (timeElapsed >= totalWarnTime) {
        setTimeoutWarning(true);
      } else {
        setTimeoutWarning(false);
      }
    }

    function handleStorageChange(event) {
      if (event.key === LAST_ACTIVITY_KEY && event.newValue) {
        const otherTabActivity = parseInt(event.newValue, 10);
        lastLocalActivityRef.current = Math.max(
          lastLocalActivityRef.current,
          otherTabActivity
        );
      }
    }

    function setEvents() {
      debouncedUpdateActivityRef.current = debounced(updateLastActivity, 100);
      events.forEach((event) => {
        window.addEventListener(event, debouncedUpdateActivityRef.current);
      });
      window.addEventListener("storage", handleStorageChange);
    }

    function destroyEvents() {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      events.forEach((event) => {
        window.removeEventListener(event, debouncedUpdateActivityRef.current);
      });
      window.removeEventListener("storage", handleStorageChange);
    }

    // Initial setup
    if (inactivityTimeoutMinutes > 0) {
      if (inactivityTimeoutMinutes < 5 || inactivityTimeoutMinutes > 480) {
        const error = new Error(
          `Inactivity timeout setting of ${inactivityTimeoutMinutes} is invalid`
        );
        mainLog(error, LOG_LEVEL.DEBUG);
      }
      updateLastActivity();
      setEvents();
      intervalRef.current = setInterval(checkTimeout, 5000); // Check every five seconds
    }

    return () => destroyEvents();
  }, [dispatch, handleLogout, inactivityTimeoutMinutes]);

  function stay() {
    setTimeoutWarning(false);
  }

  return { timeoutWarning, handleLogout, stay };
}
